import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authController } from '../controllers/authController';
import subscriptionController from '../controllers/subscriptionController';
import {
  Home, Users, UserPlus, Briefcase, CheckSquare, Mail, FileText,
  Package, ShoppingCart, DollarSign, TrendingUp, Calendar, Clock,
  Settings, Shield, Database, MessageSquare, Phone, BarChart2,
  PieChart, Activity, Folder, GitBranch, Layers, Target, Award,
  BookOpen, Bell, HelpCircle, LogOut, ChevronDown, ChevronRight,
  Menu, X, Building, CreditCard, Truck, Warehouse, ClipboardList,
  UserCheck, UserCog, FileSpreadsheet, PlusCircle, Search,
  Smartphone, Tablet, Wifi, Lock, MapPin, AlertTriangle,
  RefreshCw, Download, Upload, Trash2, Eye, EyeOff,
  Palette, Layout, Image, Type, Code, Zap, Globe, Layers as ThemeLayers,
  Languages, FileCheck, BookText, MessageCircle, Volume2,
  Wallet, ArrowUpRight, ArrowDownLeft, History, CreditCard as CardIcon,
  Gift, Receipt, BadgeDollarSign, Repeat, CircleDollarSign,
  PackageCheck, PackageX, PackageSearch, PackagePlus, PackageMinus,
  TruckIcon, ListChecks, Ban, RefreshCcw, RotateCcw, AlertOctagon,
  Group, Heart, Stethoscope, Bed, Syringe, ClipboardCheck, Ambulance,
  Pill, TestTube, FileHeart, UserCircle, BedDouble, Activity as Heartbeat,
  Thermometer, Microscope, Scan, Baby, Cross, HeartPulse, Accessibility,
  Crown, Lock as LockIcon, UserSquare, Network, Users2, UserCircle2,
  Loader2, Key, QrCode as QrCodeIcon,
  // ── NEW icons needed for expanded inventory menu ──
  Tag, Boxes,
} from 'lucide-react';

// ─── Default sections always accessible ──────────────────────────────────────
const DEFAULT_SECTIONS = ['dashboard', 'help', 'settings', 'subscriptions'];

// ─── All sections super_admin gets without subscription ───────────────────────
const ALL_SECTIONS = [
  'dashboard', 'help', 'settings', 'subscriptions',
  'hospital', 'crm', 'hrms', 'projects', 'mobile-device',
  'white-label', 'translation', 'wallet', 'orders',
  'inventory', 'sales', 'reports', 'enquiry', 'mdm',
  'communication', 'users-management',
];

export const SUBSCRIPTION_UPDATED_EVENT = 'subscriptions:updated';

const Sidebar = ({ isOpen, onClose, onToggle }) => {
  const [user,               setUser]               = useState(null);
  const [expandedMenus,      setExpandedMenus]       = useState([]);
  const [searchQuery,        setSearchQuery]         = useState('');
  const [isMobile,           setIsMobile]            = useState(false);
  const [subscribedSections, setSubscribedSections]  = useState(DEFAULT_SECTIONS);
  const [subsLoading,        setSubsLoading]         = useState(true);

  const location = useLocation();
  const navigate = useNavigate();
  const userRef  = useRef(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const loadSubscriptions = useCallback(async (currentUser) => {
    if (!currentUser) { setSubsLoading(false); return; }
    if (currentUser.role === 'super_admin') {
      setSubscribedSections(ALL_SECTIONS);
      setSubsLoading(false);
      return;
    }
    setSubsLoading(true);
    try {
      const uid = currentUser.id || currentUser._id;
      const res = await subscriptionController.getSubscribedSections(uid);
      setSubscribedSections(res.success ? res.data : DEFAULT_SECTIONS);
    } catch {
      setSubscribedSections(DEFAULT_SECTIONS);
    } finally {
      setSubsLoading(false);
    }
  }, []);

  useEffect(() => {
    const currentUser = authController.getCurrentUser();
    setUser(currentUser);
    userRef.current = currentUser;
    loadSubscriptions(currentUser);
  }, [loadSubscriptions]);

  useEffect(() => {
    const handler = () => loadSubscriptions(userRef.current);
    window.addEventListener(SUBSCRIPTION_UPDATED_EVENT, handler);
    return () => window.removeEventListener(SUBSCRIPTION_UPDATED_EVENT, handler);
  }, [loadSubscriptions]);

  useEffect(() => {
    const path = location.pathname;
    menuStructure.forEach(menu => {
      if (menu.submenus) {
        const hasActive = menu.submenus.some(sub => path.includes(sub.path));
        if (hasActive) setExpandedMenus(prev =>
          prev.includes(menu.id) ? prev : [...prev, menu.id]
        );
      }
    });
  }, [location.pathname]); // eslint-disable-line

  const handleLogout = () => { authController.logout(); navigate('/'); };

  const toggleMenu = (menuId) =>
    setExpandedMenus(prev =>
      prev.includes(menuId) ? prev.filter(id => id !== menuId) : [...prev, menuId]
    );

  const handleMenuClick = (path, sectionId, requiresSubscription) => {
    if (requiresSubscription && !subscribedSections.includes(sectionId)) {
      navigate('/dashboard/subscriptions');
      if (isMobile) onClose();
      return;
    }
    navigate(path);
    if (isMobile) onClose();
  };

  const hasAccess = (sectionId, requiresSubscription) => {
    if (user?.role === 'super_admin') return true;
    if (DEFAULT_SECTIONS.includes(sectionId)) return true;
    if (!requiresSubscription) return true;
    return subscribedSections.includes(sectionId);
  };

  // ─── Menu structure ────────────────────────────────────────────────────────
  const menuStructure = [
    {
      id: 'dashboard', title: 'Dashboard', icon: Home,
      path: '/dashboard', permissions: ['super_admin','admin','manager','employee'],
      requiresSubscription: false,
    },
    {
      id: 'users-management', title: 'Users Management', icon: Network,
      permissions: ['super_admin','admin'], requiresSubscription: true,
      submenus: [
        { id: 'all-users',            title: 'All Users',            icon: Users,         path: '/dashboard/users/all' },
        { id: 'admins',               title: 'Admins',               icon: Shield,         path: '/dashboard/users/admins' },
        { id: 'super-distributors',   title: 'Super Distributors',   icon: Award,          path: '/dashboard/users/super-distributors' },
        { id: 'distributors',         title: 'Distributors',         icon: Network,        path: '/dashboard/users/distributors' },
        { id: 'subdistributors',      title: 'Sub Distributors',     icon: Layers,         path: '/dashboard/users/subdistributors' },
        { id: 'retailers',            title: 'Retailers',            icon: ShoppingCart,   path: '/dashboard/users/retailers' },
        { id: 'clients',              title: 'Clients / End Users',  icon: UserCircle,     path: '/dashboard/users/clients' },
        { id: 'user-hierarchy',       title: 'User Hierarchy',       icon: GitBranch,      path: '/dashboard/users/hierarchy' },
        { id: 'add-user',             title: 'Add New User',         icon: UserPlus,       path: '/dashboard/users/add' },
        { id: 'bulk-import',          title: 'Bulk Import Users',    icon: Upload,         path: '/dashboard/users/bulk-import' },
        { id: 'user-permissions',     title: 'User Permissions',     icon: Shield,         path: '/dashboard/users/permissions' },
        { id: 'user-roles',           title: 'User Roles',           icon: UserCog,        path: '/dashboard/users/roles' },
        { id: 'active-users',         title: 'Active Users',         icon: UserCheck,      path: '/dashboard/users/active' },
        { id: 'inactive-users',       title: 'Inactive Users',       icon: Ban,            path: '/dashboard/users/inactive' },
        { id: 'user-activity',        title: 'User Activity Logs',   icon: Activity,       path: '/dashboard/users/activity-logs' },
        { id: 'commission-structure', title: 'Commission Structure', icon: DollarSign,     path: '/dashboard/users/commission' },
        { id: 'user-reports',         title: 'User Reports',         icon: BarChart2,      path: '/dashboard/users/reports' },
      ],
    },
    {
      id: 'hospital', title: 'Hospital Management', icon: Heart,
      permissions: ['super_admin','admin','hospital_admin','doctor','nurse'],
      requiresSubscription: true,
      submenus: [
        { id: 'patient-registration', title: 'Patient Registration', icon: UserPlus,       path: '/dashboard/hospital/patients/register' },
        { id: 'patient-records',      title: 'Patient Records',      icon: FileHeart,      path: '/dashboard/hospital/patients/records' },
        { id: 'appointments',         title: 'Appointments',         icon: Calendar,       path: '/dashboard/hospital/appointments' },
        { id: 'doctors',              title: 'Doctors',              icon: Stethoscope,    path: '/dashboard/hospital/doctors' },
        { id: 'nurses',               title: 'Nurses',               icon: UserCheck,      path: '/dashboard/hospital/nurses' },
        { id: 'departments',          title: 'Departments',          icon: Building,       path: '/dashboard/hospital/departments' },
        { id: 'wards',                title: 'Wards & Rooms',        icon: BedDouble,      path: '/dashboard/hospital/wards' },
        { id: 'bed-management',       title: 'Bed Management',       icon: Bed,            path: '/dashboard/hospital/beds' },
        { id: 'opd',                  title: 'OPD Management',       icon: ClipboardCheck, path: '/dashboard/hospital/opd' },
        { id: 'ipd',                  title: 'IPD Management',       icon: Bed,            path: '/dashboard/hospital/ipd' },
        { id: 'emergency',            title: 'Emergency',            icon: Ambulance,      path: '/dashboard/hospital/emergency' },
        { id: 'laboratory',           title: 'Laboratory',           icon: TestTube,       path: '/dashboard/hospital/laboratory' },
        { id: 'pathology',            title: 'Pathology',            icon: Microscope,     path: '/dashboard/hospital/pathology' },
        { id: 'radiology',            title: 'Radiology',            icon: Scan,           path: '/dashboard/hospital/radiology' },
        { id: 'pharmacy',             title: 'Pharmacy',             icon: Pill,           path: '/dashboard/hospital/pharmacy' },
        { id: 'blood-bank',           title: 'Blood Bank',           icon: Syringe,        path: '/dashboard/hospital/blood-bank' },
        { id: 'operation-theater',    title: 'Operation Theater',    icon: Activity,       path: '/dashboard/hospital/operation-theater' },
        { id: 'ambulance',            title: 'Ambulance Service',    icon: Ambulance,      path: '/dashboard/hospital/ambulance' },
        { id: 'icu',                  title: 'ICU Management',       icon: HeartPulse,     path: '/dashboard/hospital/icu' },
        { id: 'maternity',            title: 'Maternity Ward',       icon: Baby,           path: '/dashboard/hospital/maternity' },
        { id: 'billing',              title: 'Hospital Billing',     icon: Receipt,        path: '/dashboard/hospital/billing' },
        { id: 'insurance',            title: 'Insurance Claims',     icon: Shield,         path: '/dashboard/hospital/insurance' },
        { id: 'medical-records',      title: 'Medical Records',      icon: FileText,       path: '/dashboard/hospital/medical-records' },
        { id: 'prescriptions',        title: 'Prescriptions',        icon: FileCheck,      path: '/dashboard/hospital/prescriptions' },
        { id: 'vital-signs',          title: 'Vital Signs',          icon: Thermometer,    path: '/dashboard/hospital/vital-signs' },
        { id: 'diet-nutrition',       title: 'Diet & Nutrition',     icon: Accessibility,  path: '/dashboard/hospital/diet-nutrition' },
        { id: 'mortality',            title: 'Mortality Records',    icon: Cross,          path: '/dashboard/hospital/mortality' },
      ],
    },
    {
      id: 'crm', title: 'CRM', icon: Users,
      permissions: ['super_admin','admin','sales_manager','sales_rep'],
      requiresSubscription: true,
      submenus: [
        { id: 'leads',         title: 'Leads',         icon: UserPlus,    path: '/dashboard/crm/leads' },
        { id: 'contacts',      title: 'Contacts',      icon: Users,       path: '/dashboard/crm/contacts' },
        { id: 'accounts',      title: 'Accounts',      icon: Briefcase,   path: '/dashboard/crm/accounts' },
        { id: 'opportunities', title: 'Opportunities', icon: Target,      path: '/dashboard/crm/opportunities' },
        { id: 'deals',         title: 'Deals',         icon: DollarSign,  path: '/dashboard/crm/deals' },
        { id: 'campaigns',     title: 'Campaigns',     icon: TrendingUp,  path: '/dashboard/crm/campaigns' },
      ],
    },
    {
      id: 'enquiry', title: 'Enquiry Management', icon: Mail,
      permissions: ['super_admin','admin','sales_manager','customer_support'],
      requiresSubscription: true,
      submenus: [
        { id: 'all-enquiries', title: 'All Enquiries', icon: Mail,         path: '/dashboard/enquiry/all' },
        { id: 'pending',       title: 'Pending',       icon: Clock,        path: '/dashboard/enquiry/pending' },
        { id: 'followup',      title: 'Follow Up',     icon: Phone,        path: '/dashboard/enquiry/followup' },
        { id: 'converted',     title: 'Converted',     icon: CheckSquare,  path: '/dashboard/enquiry/converted' },
      ],
    },
    {
      id: 'hrms', title: 'HRMS', icon: UserCheck,
      permissions: ['super_admin','admin','hr_manager','hr_executive'],
      requiresSubscription: true,
      submenus: [
        { id: 'employees',   title: 'Employees',        icon: Users,      path: '/dashboard/hrms/employees' },
        { id: 'attendance',  title: 'Attendance',       icon: Calendar,   path: '/dashboard/hrms/attendance' },
        { id: 'leave',       title: 'Leave Management', icon: Clock,      path: '/dashboard/hrms/leave' },
        { id: 'payroll',     title: 'Payroll',          icon: DollarSign, path: '/dashboard/hrms/payroll' },
        { id: 'recruitment', title: 'Recruitment',      icon: UserPlus,   path: '/dashboard/hrms/recruitment' },
        { id: 'performance', title: 'Performance',      icon: Award,      path: '/dashboard/hrms/performance' },
        { id: 'training',    title: 'Training',         icon: BookOpen,   path: '/dashboard/hrms/training' },
      ],
    },
    {
      id: 'projects', title: 'Project Management', icon: Folder,
      permissions: ['super_admin','admin','project_manager','team_lead'],
      requiresSubscription: true,
      submenus: [
        { id: 'all-projects', title: 'Projects',      icon: Folder,      path: '/dashboard/projects/all' },
        { id: 'tasks',        title: 'Tasks',         icon: CheckSquare, path: '/dashboard/projects/tasks' },
        { id: 'milestones',   title: 'Milestones',    icon: Target,      path: '/dashboard/projects/milestones' },
        { id: 'gantt',        title: 'Gantt Chart',   icon: BarChart2,   path: '/dashboard/projects/gantt' },
        { id: 'timetracking', title: 'Time Tracking', icon: Clock,       path: '/dashboard/projects/timetracking' },
        { id: 'sprints',      title: 'Sprints',       icon: GitBranch,   path: '/dashboard/projects/sprints' },
        { id: 'bugs',         title: 'Bug Tracking',  icon: Activity,    path: '/dashboard/projects/bugs' },
        { id: 'team',         title: 'Team Members',  icon: Group,       path: '/dashboard/projects/team' },
      ],
    },
    {
      id: 'mobile-device', title: 'Mobile Device Management', icon: Smartphone,
      permissions: ['super_admin','admin','it_manager','device_admin'],
      requiresSubscription: true,
      submenus: [
        { id: 'customer',         title: 'All Customer',       icon: Group,         path: '/dashboard/mdm/customer' },
        { id: 'devices',          title: 'All Devices',        icon: Smartphone,    path: '/dashboard/mdm/devices' },
        { id: 'android-devices',  title: 'Android Devices',    icon: Smartphone,    path: '/dashboard/mdm/android' },
        { id: 'ios-devices',      title: 'iOS Devices',        icon: Tablet,        path: '/dashboard/mdm/ios' },
        { id: 'device-groups',    title: 'Device Groups',      icon: Layers,        path: '/dashboard/mdm/groups' },
        { id: 'device-policies',  title: 'Policies & Security',icon: Shield,        path: '/dashboard/mdm/policies' },
        { id: 'app-management',   title: 'App Management',     icon: Package,       path: '/dashboard/mdm/apps' },
        { id: 'device-location',  title: 'Device Tracking',    icon: MapPin,        path: '/dashboard/mdm/tracking' },
        { id: 'remote-lock',      title: 'Remote Lock/Wipe',   icon: Lock,          path: '/dashboard/mdm/remote-actions' },
        { id: 'device-alerts',    title: 'Alerts & Violations',icon: AlertTriangle, path: '/dashboard/mdm/alerts' },
        { id: 'device-inventory', title: 'Inventory',          icon: ClipboardList, path: '/dashboard/mdm/inventory' },
        { id: 'wifi-profiles',    title: 'WiFi Profiles',      icon: Wifi,          path: '/dashboard/mdm/wifi' },
        { id: 'device-updates',   title: 'OS Updates',         icon: RefreshCw,     path: '/dashboard/mdm/updates' },
        { id: 'compliance',       title: 'Compliance Reports', icon: FileText,      path: '/dashboard/mdm/compliance' },
      ],
    },
    {
      id: 'white-label', title: 'White Labeling', icon: Palette,
      permissions: ['super_admin','admin','brand_manager'],
      requiresSubscription: true,
      submenus: [
        { id: 'branding',              title: 'Brand Settings',       icon: Palette,    path: '/dashboard/whitelabel/branding' },
        { id: 'themes',                title: 'Theme Customization',  icon: Layout,     path: '/dashboard/whitelabel/themes' },
        { id: 'logo-management',       title: 'Logo & Icons',         icon: Image,      path: '/dashboard/whitelabel/logos' },
        { id: 'color-schemes',         title: 'Color Schemes',        icon: Palette,    path: '/dashboard/whitelabel/colors' },
        { id: 'typography',            title: 'Typography',           icon: Type,       path: '/dashboard/whitelabel/typography' },
        { id: 'email-templates',       title: 'Email Templates',      icon: Mail,       path: '/dashboard/whitelabel/email-templates' },
        { id: 'custom-domain',         title: 'Custom Domain',        icon: Globe,      path: '/dashboard/whitelabel/domain' },
        { id: 'app-customization',     title: 'App Customization',    icon: Smartphone, path: '/dashboard/whitelabel/app' },
        { id: 'qr-management',         title: 'QR Management',        icon: QrCodeIcon, path: '/dashboard/whitelabel/qr-pages' },
        { id: 'white-label-clients',   title: 'Client Configurations',icon: Users,      path: '/dashboard/whitelabel/clients' },
        { id: 'custom-css',            title: 'Custom CSS/JS',        icon: Code,       path: '/dashboard/whitelabel/custom-code' },
        { id: 'landing-pages',         title: 'Landing Pages',        icon: Layout,     path: '/dashboard/whitelabel/landing-pages' },
        { id: 'seo-settings',          title: 'SEO Settings',         icon: TrendingUp, path: '/dashboard/whitelabel/seo' },
        { id: 'integrations-branding', title: 'Integration Branding', icon: Zap,        path: '/dashboard/whitelabel/integrations' },
      ],
    },
    {
      id: 'translation', title: 'Translation Management', icon: Languages,
      permissions: ['super_admin','admin','translator','content_manager'],
      requiresSubscription: true,
      submenus: [
        { id: 'all-translations',     title: 'All Translations',   icon: Languages,   path: '/dashboard/translation/all' },
        { id: 'pending-translations', title: 'Pending',            icon: Clock,       path: '/dashboard/translation/pending' },
        { id: 'in-progress',          title: 'In Progress',        icon: RefreshCw,   path: '/dashboard/translation/in-progress' },
        { id: 'completed',            title: 'Completed',          icon: CheckSquare, path: '/dashboard/translation/completed' },
        { id: 'review',               title: 'Review & QA',        icon: FileCheck,   path: '/dashboard/translation/review' },
        { id: 'languages',            title: 'Languages',          icon: Globe,       path: '/dashboard/translation/languages' },
        { id: 'glossary',             title: 'Glossary',           icon: BookText,    path: '/dashboard/translation/glossary' },
        { id: 'translation-memory',   title: 'Translation Memory', icon: Database,    path: '/dashboard/translation/memory' },
        { id: 'translators',          title: 'Translators',        icon: Users,       path: '/dashboard/translation/translators' },
        { id: 'bulk-upload',          title: 'Bulk Upload',        icon: Upload,      path: '/dashboard/translation/bulk-upload' },
        { id: 'export',               title: 'Export',             icon: Download,    path: '/dashboard/translation/export' },
        { id: 'machine-translation',  title: 'Machine Translation',icon: Zap,         path: '/dashboard/translation/machine' },
        { id: 'voice-translation',    title: 'Voice Translation',  icon: Volume2,     path: '/dashboard/translation/voice' },
      ],
    },
    {
      id: 'wallet', title: 'Wallet & Payments', icon: Wallet,
      permissions: ['super_admin','admin','finance_manager','accountant'],
      requiresSubscription: true,
      submenus: [
        { id: 'wallet-overview',    title: 'Wallet Overview',    icon: Wallet,           path: '/dashboard/wallet/overview' },
        { id: 'balance',            title: 'Balance',            icon: CircleDollarSign, path: '/dashboard/wallet/balance' },
        { id: 'add-money',          title: 'Add Money',          icon: ArrowDownLeft,    path: '/dashboard/wallet/add-money' },
        { id: 'withdraw',           title: 'Withdraw',           icon: ArrowUpRight,     path: '/dashboard/wallet/withdraw' },
        { id: 'transactions',       title: 'Transactions',       icon: History,          path: '/dashboard/wallet/transactions' },
        { id: 'payment-methods',    title: 'Payment Methods',    icon: CardIcon,         path: '/dashboard/wallet/payment-methods' },
        { id: 'bank-accounts',      title: 'Bank Accounts',      icon: Building,         path: '/dashboard/wallet/bank-accounts' },
        { id: 'cards',              title: 'Cards',              icon: CreditCard,       path: '/dashboard/wallet/cards' },
        { id: 'recurring-payments', title: 'Recurring Payments', icon: Repeat,           path: '/dashboard/wallet/recurring' },
        { id: 'invoices-wallet',    title: 'Invoices',           icon: Receipt,          path: '/dashboard/wallet/invoices' },
        { id: 'rewards',            title: 'Rewards & Cashback', icon: Gift,             path: '/dashboard/wallet/rewards' },
        { id: 'statements',         title: 'Statements',         icon: FileText,         path: '/dashboard/wallet/statements' },
        { id: 'limits',             title: 'Limits & KYC',       icon: Shield,           path: '/dashboard/wallet/limits' },
        { id: 'key-wallet',         title: 'Key Wallet',         icon: Key,              path: '/dashboard/wallet/keys' },
      ],
    },
    {
      id: 'orders', title: 'Order Management', icon: PackageCheck,
      permissions: ['super_admin','admin','order_manager','warehouse_staff'],
      requiresSubscription: true,
      submenus: [
        { id: 'all-orders',       title: 'All Orders',       icon: PackageCheck, path: '/dashboard/orders/all' },
        { id: 'new-orders',       title: 'New Orders',       icon: PackagePlus,  path: '/dashboard/orders/new' },
        { id: 'pending-orders',   title: 'Pending',          icon: Clock,        path: '/dashboard/orders/pending' },
        { id: 'confirmed-orders', title: 'Confirmed',        icon: CheckSquare,  path: '/dashboard/orders/confirmed' },
        { id: 'processing',       title: 'Processing',       icon: RefreshCcw,   path: '/dashboard/orders/processing' },
        { id: 'packed',           title: 'Packed',           icon: Package,      path: '/dashboard/orders/packed' },
        { id: 'shipped',          title: 'Shipped',          icon: TruckIcon,    path: '/dashboard/orders/shipped' },
        { id: 'out-for-delivery', title: 'Out for Delivery', icon: Truck,        path: '/dashboard/orders/out-for-delivery' },
        { id: 'delivered',        title: 'Delivered',        icon: PackageCheck, path: '/dashboard/orders/delivered' },
        { id: 'cancelled',        title: 'Cancelled',        icon: Ban,          path: '/dashboard/orders/cancelled' },
        { id: 'returns',          title: 'Returns',          icon: RotateCcw,    path: '/dashboard/orders/returns' },
        { id: 'failed',           title: 'Failed',           icon: PackageX,     path: '/dashboard/orders/failed' },
        { id: 'track-order',      title: 'Track Order',      icon: PackageSearch,path: '/dashboard/orders/track' },
        { id: 'order-issues',     title: 'Order Issues',     icon: AlertOctagon, path: '/dashboard/orders/issues' },
      ],
    },
    {
      id: 'mdm', title: 'Master Data', icon: Database,
      permissions: ['super_admin','admin','data_manager'],
      requiresSubscription: true,
      submenus: [
        { id: 'products',   title: 'Product Master',  icon: Package,  path: '/dashboard/master-data/products' },
        { id: 'customers',  title: 'Customer Master', icon: Users,    path: '/dashboard/master-data/customers' },
        { id: 'vendors',    title: 'Vendor Master',   icon: Truck,    path: '/dashboard/master-data/vendors' },
        { id: 'locations',  title: 'Location Master', icon: Building, path: '/dashboard/master-data/locations' },
        { id: 'categories', title: 'Category Master', icon: Layers,   path: '/dashboard/master-data/categories' },
      ],
    },

    // ─────────────────────────────────────────────────────────────────────────
    // INVENTORY — EXPANDED (19 submenus)
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: 'inventory', title: 'Inventory', icon: Warehouse,
      permissions: ['super_admin','admin','inventory_manager','warehouse_manager'],
      requiresSubscription: true,
      submenus: [
        // ── Product Catalog ──────────────────────────────────────────────────
        { id: 'inv-categories',      title: 'Categories',         icon: Tag,          path: '/dashboard/inventory/categories' },
        { id: 'inv-sub-categories',  title: 'Sub Categories',     icon: Layers,       path: '/dashboard/inventory/sub-categories' },
        { id: 'inv-brands',          title: 'Brands',             icon: Boxes,        path: '/dashboard/inventory/brands' },
        { id: 'inv-units',           title: 'Units of Measure',   icon: Settings,     path: '/dashboard/inventory/units' },
        { id: 'inv-products',        title: 'Products',           icon: Package,      path: '/dashboard/inventory/products' },

        // ── Stock Operations ─────────────────────────────────────────────────
        { id: 'stock',               title: 'Stock Management',   icon: BarChart2,    path: '/dashboard/inventory/stock' },
        { id: 'inv-stock-in',        title: 'Stock In',           icon: PackageCheck, path: '/dashboard/inventory/stock-in' },
        { id: 'inv-stock-out',       title: 'Stock Out',          icon: PackageMinus, path: '/dashboard/inventory/stock-out' },
        { id: 'transfer',            title: 'Stock Transfer',     icon: Truck,        path: '/dashboard/inventory/transfer' },
        { id: 'adjustment',          title: 'Stock Adjustment',   icon: ClipboardList,path: '/dashboard/inventory/adjustment' },

        // ── Procurement ──────────────────────────────────────────────────────
        { id: 'inv-suppliers',       title: 'Suppliers',          icon: Users,        path: '/dashboard/inventory/suppliers' },
        { id: 'purchase-orders',     title: 'Purchase Orders',    icon: ShoppingCart, path: '/dashboard/inventory/purchase-orders' },
        { id: 'inv-purchase-returns',title: 'Purchase Returns',   icon: RotateCcw,    path: '/dashboard/inventory/purchase-returns' },

        // ── Warehousing ──────────────────────────────────────────────────────
        { id: 'inv-warehouses',      title: 'Warehouses',         icon: Warehouse,    path: '/dashboard/inventory/warehouses' },
        { id: 'inv-locations',       title: 'Storage Locations',  icon: MapPin,       path: '/dashboard/inventory/locations' },

        // ── Alerts & Reports ─────────────────────────────────────────────────
        { id: 'inv-low-stock',       title: 'Low Stock Alerts',   icon: AlertTriangle,path: '/dashboard/inventory/low-stock' },
        { id: 'inv-expiry-alerts',   title: 'Expiry Alerts',      icon: Receipt,      path: '/dashboard/inventory/expiry-alerts' },
        { id: 'inv-reports',         title: 'Inventory Reports',  icon: BarChart2,    path: '/dashboard/inventory/reports' },
        { id: 'inv-barcode',         title: 'Barcode / QR',       icon: QrCodeIcon,   path: '/dashboard/inventory/barcode' },
      ],
    },

    {
      id: 'sales', title: 'Sales', icon: ShoppingCart,
      permissions: ['super_admin','admin','sales_manager','sales_rep'],
      requiresSubscription: true,
      submenus: [
        { id: 'quotations', title: 'Quotations',   icon: FileText,       path: '/dashboard/sales/quotations' },
        { id: 'orders',     title: 'Sales Orders', icon: ShoppingCart,   path: '/dashboard/sales/orders' },
        { id: 'invoices',   title: 'Invoices',     icon: FileSpreadsheet,path: '/dashboard/sales/invoices' },
        { id: 'payments',   title: 'Payments',     icon: CreditCard,     path: '/dashboard/sales/payments' },
      ],
    },
    {
      id: 'reports', title: 'Reports', icon: BarChart2,
      permissions: ['super_admin','admin','manager'],
      requiresSubscription: true,
      submenus: [
        { id: 'sales-reports',    title: 'Sales Reports',    icon: TrendingUp,   path: '/dashboard/reports/sales' },
        { id: 'hospital-reports', title: 'Hospital Reports', icon: Heart,        path: '/dashboard/reports/hospital' },
        { id: 'hr-reports',       title: 'HR Reports',       icon: Users,        path: '/dashboard/reports/hr' },
        { id: 'project-reports',  title: 'Project Reports',  icon: Folder,       path: '/dashboard/reports/projects' },
        { id: 'device-reports',   title: 'Device Reports',   icon: Smartphone,   path: '/dashboard/reports/devices' },
        { id: 'order-reports',    title: 'Order Reports',    icon: PackageCheck, path: '/dashboard/reports/orders' },
        { id: 'wallet-reports',   title: 'Wallet Reports',   icon: Wallet,       path: '/dashboard/reports/wallet' },
        { id: 'user-reports',     title: 'User Reports',     icon: Network,      path: '/dashboard/reports/users' },
        { id: 'financial',        title: 'Financial Reports',icon: DollarSign,   path: '/dashboard/reports/financial' },
        { id: 'custom',           title: 'Custom Reports',   icon: PieChart,     path: '/dashboard/reports/custom' },
      ],
    },
    {
      id: 'communication', title: 'Communication', icon: MessageSquare,
      permissions: ['super_admin','admin','employee'],
      requiresSubscription: true,
      submenus: [
        { id: 'messages',      title: 'Messages',      icon: MessageSquare, path: '/dashboard/communication/messages' },
        { id: 'announcements', title: 'Announcements', icon: Bell,          path: '/dashboard/communication/announcements' },
        { id: 'notifications', title: 'Notifications', icon: Bell,          path: '/dashboard/communication/notifications' },
      ],
    },
    {
      id: 'subscriptions', title: 'Subscriptions', icon: Crown,
      path: '/dashboard/subscriptions',
      permissions: ['super_admin','admin'], requiresSubscription: false,
    },
    {
      id: 'settings', title: 'Settings', icon: Settings,
      permissions: ['super_admin','admin','retailer'], requiresSubscription: false,
      submenus: [
        { id: 'profile',      title: 'Profile Settings',    icon: Building,   path: '/dashboard/settings/profile' },
        { id: 'company',      title: 'Company Settings',    icon: Building,   path: '/dashboard/settings/company' },
        { id: 'users',        title: 'User Management',     icon: UserCog,    path: '/dashboard/settings/users' },
        { id: 'roles',        title: 'Roles & Permissions', icon: Shield,     path: '/dashboard/settings/roles' },
        { id: 'system',       title: 'System Settings',     icon: Settings,   path: '/dashboard/settings/system' },
        { id: 'integrations', title: 'Integrations',        icon: PlusCircle, path: '/dashboard/settings/integrations' },
      ],
    },
    {
      id: 'help', title: 'Help & Support', icon: HelpCircle,
      path: '/dashboard/help',
      permissions: ['super_admin','admin','employee'], requiresSubscription: false,
    },
  ];

  const filterMenus = (menus) => {
    if (!searchQuery) return menus;
    return menus.filter(menu => {
      const matchTitle = menu.title.toLowerCase().includes(searchQuery.toLowerCase());
      const matchSub   = menu.submenus?.some(s => s.title.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchTitle || matchSub;
    });
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Mobile overlay */}
      <div
        style={{ display: isMobile && isOpen ? 'block' : 'none', position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 998 }}
        onClick={onClose}
      />

      <div style={{
        position: 'fixed', left: 0, top: 0, bottom: 0, width: 280,
        backgroundColor: '#1e293b', color: '#fff', zIndex: 999,
        display: 'flex', flexDirection: 'column',
        transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s ease', overflowY: 'auto',
      }}>

        {/* Header */}
        {/* Header */}
<div style={{ padding: 20, borderBottom: '1px solid #334155' }}>
  <div style={{ fontSize: 22, fontWeight: 700, color: '#3b82f6', marginBottom: 16, textAlign: 'center' }}>
    TRACKLIO HUB
  </div>
  {user && (
    <div
      onClick={() => {
        navigate('/dashboard/settings/profile');
        if (isMobile) onClose();
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          navigate('/dashboard/settings/profile');
          if (isMobile) onClose();
        }
      }}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: 12,
        backgroundColor: '#334155', borderRadius: 8,
        cursor: 'pointer', transition: 'background-color 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#3f4d63'}
      onMouseLeave={e => e.currentTarget.style.backgroundColor = '#334155'}
      title="View profile"
    >
      <div style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, overflow: 'hidden', flexShrink: 0 }}>
        {user?.avatar
          ? <img src={user.avatar} alt={user.fullName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : (user?.fullName?.charAt(0).toUpperCase() || 'U')}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.fullName || 'User'}</div>
        <div style={{ fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
          {user.role === 'super_admin' && <Crown size={11} color="#f59e0b" />}
          {user.role || 'Employee'}
        </div>
      </div>
      {subsLoading && <Loader2 size={14} color="#94a3b8" style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />}
      <ChevronRight size={14} color="#64748b" style={{ flexShrink: 0 }} />
    </div>
  )}
</div>

        {/* Search */}
        <div style={{ margin: '12px 16px', position: 'relative' }}>
          <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Search menus..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '9px 9px 9px 34px', backgroundColor: '#334155', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>

        {/* Menu list */}
        <div style={{ flex: 1, padding: '4px 8px', overflowY: 'auto' }}>
          {filterMenus(menuStructure).map((menu) => {
            const access = hasAccess(menu.id, menu.requiresSubscription);

            return (
              <div key={menu.id} style={{ marginBottom: 2 }}>
                {menu.submenus ? (
                  <>
                    <button
                      title={!access ? `Subscribe to unlock ${menu.title}` : ''}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', padding: '10px 14px',
                        backgroundColor: expandedMenus.includes(menu.id) ? '#334155' : 'transparent',
                        border: 'none', color: access ? '#e2e8f0' : '#64748b',
                        cursor: access ? 'pointer' : 'not-allowed',
                        borderRadius: 8, fontSize: 13, transition: 'background-color 0.15s',
                      }}
                      onClick={() => access ? toggleMenu(menu.id) : navigate('/dashboard/subscriptions')}
                      onMouseEnter={e => { if (access) e.currentTarget.style.backgroundColor = '#334155'; }}
                      onMouseLeave={e => { if (!expandedMenus.includes(menu.id)) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <menu.icon size={17} />
                        <span>{menu.title}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {!access && <LockIcon size={13} color="#64748b" />}
                        {access && (expandedMenus.includes(menu.id)
                          ? <ChevronDown size={15} />
                          : <ChevronRight size={15} />)}
                      </div>
                    </button>

                    {expandedMenus.includes(menu.id) && access && (
                      <div style={{ marginLeft: 12, marginTop: 2, marginBottom: 4 }}>
                        {menu.submenus.map(sub => (
                          <div
                            key={sub.id}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '9px 14px', borderRadius: 6, fontSize: 12,
                              color: isActive(sub.path) ? '#fff' : '#cbd5e1',
                              backgroundColor: isActive(sub.path) ? '#3b82f6' : 'transparent',
                              cursor: 'pointer', marginBottom: 1, transition: 'background-color 0.15s',
                            }}
                            onClick={() => handleMenuClick(sub.path, menu.id, menu.requiresSubscription)}
                            onMouseEnter={e => { if (!isActive(sub.path)) e.currentTarget.style.backgroundColor = '#334155'; }}
                            onMouseLeave={e => { if (!isActive(sub.path)) e.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            <sub.icon size={15} />
                            <span>{sub.title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <button
                    title={!access ? `Subscribe to unlock ${menu.title}` : ''}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', padding: '10px 14px',
                      backgroundColor: isActive(menu.path) ? '#3b82f6' : 'transparent',
                      border: 'none', color: access ? '#e2e8f0' : '#64748b',
                      cursor: access ? 'pointer' : 'not-allowed',
                      borderRadius: 8, fontSize: 13, transition: 'background-color 0.15s',
                    }}
                    onClick={() => handleMenuClick(menu.path, menu.id, menu.requiresSubscription)}
                    onMouseEnter={e => { if (!isActive(menu.path) && access) e.currentTarget.style.backgroundColor = '#334155'; }}
                    onMouseLeave={e => { if (!isActive(menu.path)) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <menu.icon size={17} />
                      <span>{menu.title}</span>
                    </div>
                    {!access && <LockIcon size={13} color="#64748b" />}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Logout */}
        <div style={{ padding: 16, borderTop: '1px solid #334155' }}>
          <button
            onClick={handleLogout}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 11, backgroundColor: '#dc2626', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#b91c1c'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#dc2626'}
          >
            <LogOut size={17} />
            Logout
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </>
  );
};

export default Sidebar;